// Petit client API pour BCOOLSTORE (frontend → FastAPI)
// Toutes les URLs passent par REACT_APP_BACKEND_URL (variable protégée).

const BASE = process.env.REACT_APP_BACKEND_URL;
if (!BASE) {
  // eslint-disable-next-line no-console
  console.error('[api] REACT_APP_BACKEND_URL manquant dans frontend/.env');
}

const API = `${BASE}/api`;

const ADMIN_HEADER = 'X-Admin-Password';

function getAdminPassword() {
  return localStorage.getItem('bcool_admin_pwd') || '';
}

async function request(path, { method = 'GET', body, admin = false, signal } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (admin) {
    const pwd = getAdminPassword();
    if (pwd) headers[ADMIN_HEADER] = pwd;
  }
  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  if (res.status === 204) return null;
  let payload = null;
  try { payload = await res.json(); } catch (e) { /* ignore */ }
  if (!res.ok) {
    const err = new Error(payload?.detail || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

// ---------- STORIES ----------
export const StoriesAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.category) qs.set('category', params.category);
    if (params.limit != null) qs.set('limit', params.limit);
    if (params.offset != null) qs.set('offset', params.offset);
    if (params.order) qs.set('order', params.order);
    if (params.published != null) qs.set('published', params.published);
    const q = qs.toString() ? `?${qs}` : '';
    return request(`/stories${q}`);
  },
  search: (q, limit = 20) =>
    request(`/stories/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  getBySlug: (slug) => request(`/stories/slug/${encodeURIComponent(slug)}`),
  getById: (id) => request(`/stories/id/${encodeURIComponent(id)}`),
  create: (payload) => request('/stories', { method: 'POST', body: payload, admin: true }),
  update: (id, patch) => request(`/stories/${encodeURIComponent(id)}`, { method: 'PUT', body: patch, admin: true }),
  remove: (id) => request(`/stories/${encodeURIComponent(id)}`, { method: 'DELETE', admin: true }),
};

// ---------- ADMIN AUTH ----------
export const AdminAPI = {
  login: async (password) => {
    // Tries to POST a harmless request that requires admin; on 200/409 → ok, on 401/403 → wrong pwd
    const res = await fetch(`${API}/admin/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', [ADMIN_HEADER]: password },
    });
    if (res.ok) {
      localStorage.setItem('bcool_admin_pwd', password);
      return true;
    }
    if (res.status === 401 || res.status === 403) return false;
    // Unexpected error
    const t = await res.text();
    throw new Error(`Erreur inattendue (${res.status}): ${t}`);
  },
  logout: () => localStorage.removeItem('bcool_admin_pwd'),
  isLoggedIn: () => !!getAdminPassword(),
};

// ---------- UPLOAD ----------
export const UploadAPI = {
  // Upload a File/Blob; returns { url, filename, size_bytes, size_kb }
  image: async (file, { onProgress } = {}) => {
    const pwd = getAdminPassword();
    if (!pwd) throw new Error('Non authentifié.');
    const form = new FormData();
    form.append('file', file);

    // Use XHR for progress support (fetch doesn't expose upload progress)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/upload/image`);
      xhr.setRequestHeader(ADMIN_HEADER, pwd);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        let body = null;
        try { body = JSON.parse(xhr.responseText); } catch (e) { /* ignore */ }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(body);
        } else {
          const err = new Error(body?.detail || `HTTP ${xhr.status}`);
          err.status = xhr.status;
          err.payload = body;
          reject(err);
        }
      };
      xhr.onerror = () => reject(new Error('Erreur réseau pendant l’upload.'));
      xhr.send(form);
    });
  },
};

// ---------- CATEGORIES ----------
export const CategoriesAPI = {
  list: () => request('/categories'),
};

const api = { StoriesAPI, CategoriesAPI, AdminAPI, UploadAPI };
export default api;
